import api from '@/utils/axios';
import { fetchEmployeeProfileOptions } from '@/api/employeesApi';

const optionsCache = new Map();
const optionsCachePromise = new Map();

const cacheKeyFor = (includeInactive) => (includeInactive ? 'with_inactive' : 'active_only');

/**
 * Org reference reads — temporary reuse of employee profile options endpoint.
 * See docs/ORG_REFERENCE_API.md §UI reads and docs/SETTINGS_ORG_REFERENCE_LIST_PATTERN.md
 */
export async function fetchOrgReferenceOptions({ force = false, includeInactive = false } = {}) {
  const cacheKey = cacheKeyFor(includeInactive);

  if (!force && optionsCache.has(cacheKey)) {
    return optionsCache.get(cacheKey);
  }
  if (!force && optionsCachePromise.has(cacheKey)) {
    return optionsCachePromise.get(cacheKey);
  }

  const promise = fetchEmployeeProfileOptions({ include_inactive: includeInactive ? 1 : undefined })
    .then((data) => {
      optionsCache.set(cacheKey, data);
      return data;
    })
    .finally(() => {
      optionsCachePromise.delete(cacheKey);
    });

  optionsCachePromise.set(cacheKey, promise);
  return promise;
}

export function invalidateOrgReferenceOptions() {
  optionsCache.clear();
  optionsCachePromise.clear();
}

async function postManage(route, action, payload) {
  try {
    const response = await api.post(route, { action, ...payload });
    if (!response.data?.success) {
      const error = new Error(response.data?.error?.message || 'Request failed');
      error.code = response.data?.error?.code || null;
      throw error;
    }
    return response.data.data;
  } catch (err) {
    if (err instanceof Error && err.message && !err.error) {
      throw err;
    }
    const message = err?.message || err?.error?.message || 'Request failed';
    const error = new Error(message);
    error.code = err?.response?.data?.error?.code || err?.code || null;
    throw error;
  }
}

export const orgReferenceApi = {
  locations: {
    create: (payload) => postManage('locations/manage', 'create', payload),
    update: (payload) => postManage('locations/manage', 'update', payload),
    delete: ({ location_id }) => postManage('locations/manage', 'delete', { location_id }),
  },
  subDepartments: {
    create: (payload) => postManage('sub-departments/manage', 'create', payload),
    update: (payload) => postManage('sub-departments/manage', 'update', payload),
    delete: ({ sub_department_id }) =>
      postManage('sub-departments/manage', 'delete', { sub_department_id }),
  },
  teams: {
    create: (payload) => postManage('teams/manage', 'create', payload),
    update: (payload) => postManage('teams/manage', 'update', payload),
    delete: ({ team_id }) => postManage('teams/manage', 'delete', { team_id }),
  },
};
